import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { CONTROL_CENTRE_API_DOMAIN } from '@/config/constants'
import useAuthenticatedMutation from '@/hooks/useAuthenticatedMutation'
import { useAuthStore } from '@/stores/auth.store'
import { useControlCenterStore } from '@/stores/controlCenter.store'
import type { AdminApplicationItem, Alert } from '@/types/controlCenter'
import { cn } from '@/utils/cn'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { QUERY_KEYS } from '../utils/queryKeys'
import EditAlertModal from '../components/EditAlertModal'
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal'
import RowActions from '../components/shared/RowActions'
import SortIcon, { type SortDir } from '../components/shared/SortIcon'
import TablePagination from '../components/shared/TablePagination'
import { thCls, thSortBtnCls, tdCls } from '../components/shared/tableStyles'
import { fmtDate, fmtDateTime, cap } from '../utils/format'
import {
  COLUMN_CONFIG,
  PAGE_SIZE,
  SEVERITY_BADGE,
  SEVERITY_ORDER,
  STATUS_BADGE,
  STATUS_DOT,
} from './constants'
import useAlertsData from './hooks/useAlertsData'
import type { SortField } from './types'

interface AlertsProps {
  applications?: AdminApplicationItem[]
  clientNameToId?: Record<string, number>
  isCreating?: boolean
  onCreateClose?: () => void
}

// ── Status badge sub-component ─────────────────────────────────────────────────

const StatusBadge = ({ status }: { status: Alert['status'] }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold capitalize',
      STATUS_BADGE[status],
    )}
  >
    <span className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT[status])} />
    {status}
  </span>
)

// ── Main component ─────────────────────────────────────────────────────────────

const Alerts = ({
  applications = [],
  clientNameToId = {},
  isCreating = false,
  onCreateClose,
}: AlertsProps) => {
  const queryClient = useQueryClient()
  const { alertsSearchQuery, alertSeverityFilter, alertStatusFilter } =
    useControlCenterStore()
  const role = useAuthStore((s) => s.permissions?.role)

  const { alerts } = useAlertsData()

  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [page, setPage] = useState(0)
  const [editingAlert, setEditingAlert] = useState<Alert | null>(null)
  const [deletingAlert, setDeletingAlert] = useState<Alert | null>(null)

  useEffect(() => {
    setPage(0)
  }, [alertsSearchQuery, alertSeverityFilter, alertStatusFilter])

  const deleteMutation = useAuthenticatedMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.alerts,
      })
      setDeletingAlert(null)
    },
  })

  const toggleSort = (field: SortField) => {
    if (field === sortField) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else {
      setSortField(field)
      setSortDir('asc')
    }
    setPage(0)
  }

  const q = alertsSearchQuery.toLowerCase()
  const filtered = alerts
    .filter(
      (a) =>
        (alertSeverityFilter === 'all' || a.severity === alertSeverityFilter) &&
        (alertStatusFilter === 'all' || a.status === alertStatusFilter) &&
        (q === '' ||
          a.name.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q)),
    )
    .sort((a, b) => {
      if (sortField === 'endDate') {
        if (!a.endDate && !b.endDate) return 0
        if (!a.endDate) return sortDir === 'asc' ? 1 : -1
        if (!b.endDate) return sortDir === 'asc' ? -1 : 1
      }
      const va =
        sortField === 'severity'
          ? SEVERITY_ORDER[a.severity]
          : (a[sortField] as string)
      const vb =
        sortField === 'severity'
          ? SEVERITY_ORDER[b.severity]
          : (b[sortField] as string)
      if (va < vb) return sortDir === 'asc' ? -1 : 1
      if (va > vb) return sortDir === 'asc' ? 1 : -1
      return 0
    })

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
  // SUPER_ADMIN and ADMIN can both reach alerts (enforced by RequireRole).
  // Both may edit; only SUPER_ADMIN may delete.
  const canAct = role === 'SUPER_ADMIN' || role === 'ADMIN'

  return (
    <div className='flex flex-1 overflow-hidden'>
      <div className='border-border-main bg-neutral-dimmed-heavy flex flex-1 flex-col overflow-hidden rounded-lg border'>
        {/* Table */}
        <div className='flex-1 overflow-auto'>
          <Table className='table-auto'>
            <TableHeader className='border-border-main bg-neutral-dimmed sticky top-0 border-b'>
              <TableRow className='border-border-main hover:bg-transparent'>
                {COLUMN_CONFIG.map((col) => {
                  const isSortable = col.sortable && col.key !== 'actions'
                  const isActive = sortField === col.key
                  return (
                    <TableHead
                      key={col.key}
                      className={cn(thCls, col.className)}
                      aria-sort={
                        isSortable
                          ? isActive
                            ? sortDir === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                          : undefined
                      }
                    >
                      {isSortable ? (
                        <button
                          type='button'
                          className={thSortBtnCls}
                          onClick={() => toggleSort(col.key as SortField)}
                        >
                          {col.label}
                          <SortIcon
                            field={col.key}
                            sortField={sortField}
                            sortDir={sortDir}
                          />
                        </button>
                      ) : (
                        col.label
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            </TableHeader>
            <TableBody className='divide-border-main divide-y'>
              {paged.map((alert) => (
                <TableRow
                  key={alert.id}
                  className='border-border-main hover:bg-neutral-dimmed transition'
                >
                  <TableCell
                    className={cn(
                      tdCls,
                      'w-[35%] max-w-[35%] min-w-[35%] overflow-hidden',
                    )}
                  >
                    <p className='text-text-primary font-medium break-all'>
                      {alert.name}
                    </p>
                    <p className='text-text-tertiary mt-0.5 text-xs break-all'>
                      {alert.description}
                    </p>
                  </TableCell>
                  <TableCell className={tdCls}>
                    <span
                      className={cn(
                        'rounded px-2 py-0.5 text-xs font-bold tracking-wide uppercase',
                        SEVERITY_BADGE[alert.severity],
                      )}
                    >
                      {cap(alert.severity)}
                    </span>
                  </TableCell>
                  <TableCell
                    className={cn(
                      tdCls,
                      'w-[20%] max-w-[20%] min-w-[20%] overflow-hidden',
                    )}
                  >
                    <span className='text-text-primary font-mono text-sm break-all'>
                      {alert.url}
                    </span>
                  </TableCell>
                  <TableCell className={tdCls}>
                    <StatusBadge status={alert.status} />
                  </TableCell>
                  <TableCell className={cn(tdCls, 'whitespace-nowrap')}>
                    <span className='text-text-tertiary text-sm'>
                      {fmtDate(alert.createdAt)}
                    </span>
                  </TableCell>
                  <TableCell className={cn(tdCls, 'whitespace-nowrap')}>
                    {alert.endDate ? (
                      <span className='text-text-tertiary text-sm'>
                        {fmtDateTime(alert.endDate)}
                      </span>
                    ) : (
                      <span className='text-text-dimmed text-sm'>—</span>
                    )}
                  </TableCell>
                  <TableCell className={tdCls}>
                    <RowActions
                      itemName={alert.name}
                      onEdit={canAct ? () => setEditingAlert(alert) : undefined}
                      onDelete={
                        role === 'SUPER_ADMIN'
                          ? () => setDeletingAlert(alert)
                          : undefined
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
              {paged.length === 0 && (
                <TableRow className='hover:bg-transparent'>
                  <TableCell
                    colSpan={COLUMN_CONFIG.length}
                    className='text-text-tertiary py-12 text-center text-sm'
                  >
                    No alerts match your filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <TablePagination
            page={page}
            totalPages={totalPages}
            onPrev={() => setPage(page - 1)}
            onNext={() => setPage(page + 1)}
          />
        )}
      </div>

      {(editingAlert || isCreating) && (
        <EditAlertModal
          alert={editingAlert ?? undefined}
          applications={applications}
          clientNameToId={clientNameToId}
          onClose={() => {
            setEditingAlert(null)
            onCreateClose?.()
          }}
        />
      )}

      {deletingAlert && (
        <ConfirmDeleteModal
          title='Delete Alert'
          itemName={deletingAlert.name}
          isPending={deleteMutation.isPending}
          error={deleteMutation.error}
          onConfirm={() =>
            deleteMutation.mutate({
              method: 'delete',
              url: `${CONTROL_CENTRE_API_DOMAIN}/admin/alerts/${deletingAlert.id}`,
              data: {},
            })
          }
          onCancel={() => setDeletingAlert(null)}
        />
      )}
    </div>
  )
}

export default Alerts
